import clsx from 'clsx';
import { useAuth } from 'context/auth';
import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { CasinoSlotReviewModal } from 'components/modal/casino-slot-review';
import { LoginModal } from 'components/modal/login';
import { Anchor } from 'constants/anchors';
import { RedirectActions } from 'constants/redirect-actions';
import { useLoginModal, useRedirectActions } from 'hooks';
import _ from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { saveRedirectOptions } from 'utils/redirectOptions';
import { ReviesUser } from '../template/slot-page/components/revies-user';

import { LoadMoreButton } from 'components/load-more-button';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { Header } from 'components/slider/header';
import { Analytics } from 'constants/analytics';
import { Routes } from 'constants/routes';
import { ICasino, IProvider, ISlot, ISlotReview } from 'constants/types';
import { getItemIdentifier } from 'utils/slug';
import styles from './styles.module.css';
import { ReviewSlot } from 'components/cards/slot';

interface SlotWithRating extends ISlot {
  averageRating: number;
}
interface ReviewItem extends ISlotReview {
  casino: ICasino;
  slot: SlotWithRating;
  provider: IProvider;
}
interface IGroupedSlot {
  [key: string]: ReviewItem[];
}
interface IGroupedData {
  [key: string]: IGroupedSlot;
}

const groupByProvidersAndSlots = (data: ISlotReview[]) => {
  const groupedByProviders: any = _.groupBy(
    data,
    (item: any) => (item as ISlotReview & { provider: IProvider }).provider._id
  );

  _.forEach(groupedByProviders, (value: any, key: any) => {
    groupedByProviders[key] = _.groupBy(value, 'slot_id');
  });

  return groupedByProviders as IGroupedData;
};

export const Review = ({
  limit,
  userReviews,
  underReview,
}: {
  limit?: number;
  underReview?: boolean;
  userReviews?: ReviewItem[];
}) => {
  const isSlotReviews = !!userReviews?.[0]?.casino;

  const reviews = useMemo(() => userReviews ?? [], [userReviews]);
  const reviewItem: any = isSlotReviews
    ? userReviews?.[0]?.casino
    : userReviews?.[0]?.slot;
  const averageRating = reviewItem?.averageRating ?? 0;

  const { averageRatingHint, averageRatingClass } = useMemo(() => {
    if (averageRating >= 95) {
      return {
        averageRatingHint: 'high RTP',
        averageRatingClass: styles.highRtp,
      };
    }

    return {
      averageRatingHint: 'low RTP',
      averageRatingClass: styles.lowRtp,
    };
  }, [averageRating]);

  const { isDifferenseVisible, percentageValue, differenceValue, sign } =
    useMemo(() => {
      const [last, preLast] = reviews;

      const percentage = Number(
        (((last?.value ?? 0) * 100) / (preLast?.value ?? 0))?.toFixed(2)
      );

      const percantageValue =
        percentage >= 100 ? percentage - 100 : -percentage;
      const difference = last?.value - preLast?.value;
      const differenceValueSign: string =
        difference > 0 ? '+' : difference < 0 ? '-' : '';
      return {
        percentageValue: percantageValue ?? 0,
        differenceValue: difference,
        isDifferenseVisible: reviews?.length > 1,
        sign: differenceValueSign,
      };
    }, [reviews]);

  return (
    <div className={styles.reviews__column}>
      <div
        className={clsx(
          styles.reviews__review,
          styles.slot__review,
          styles.slot__review_increase
        )}
      >
        <Link
          href={
            isSlotReviews
              ? Routes.casinoPage(getItemIdentifier(reviewItem))
              : Routes.slotPage(getItemIdentifier(reviewItem))
          }
          className={styles.slot_review__slot_link}
        >
          <div className={styles.slot_review__info}>
            <div className={styles.slot_review__photo}>
              <LogoOverlayWrapper>
                {isSlotReviews && (
                  <LogoOverlay
                    width={60}
                    height={60}
                    alt={reviewItem?.logoUrl ?? ''}
                    image={reviewItem?.logoUrl ?? ''}
                  />
                )}
                <Image
                  src={reviewItem?.imageUrl ?? ''}
                  width={100}
                  height={100}
                  alt="review-image"
                  style={{ width: '100%', height: 'auto' }}
                />
              </LogoOverlayWrapper>
            </div>
            <div className={styles.slot_review__about}>
              <div className={styles.slot_review__name}>{reviewItem.name}</div>
              {isDifferenseVisible && (
                <div
                  className={clsx({
                    [styles.slot_review__status]: true,
                    [styles.slot__review_increase_color]: differenceValue >= 0,
                    [styles.slot__review_decrease]: differenceValue < 0,
                  })}
                >
                  <Icon.TradeArrow />
                  <span>{`${sign}${differenceValue?.toFixed(
                    2
                  )} (${percentageValue.toFixed(2)}%)`}</span>
                </div>
              )}
            </div>
          </div>
          <div className={clsx(styles.slot_review__rtpi, averageRatingClass)}>
            {`${(averageRating ?? 0)?.toFixed(2)}%`}
            <div className={clsx(styles.ratingHint, averageRatingClass)}>
              <span>{averageRatingHint}</span>
            </div>
          </div>
        </Link>

        {reviews
          .slice(0, limit)
          ?.map((review) => (
            <ReviesUser
              hasLowRating={reviews?.some(
                (item) => item.value < 95 && averageRating < 95
              )}
              key={review._id}
              data={review}
            />
          ))}

        {!isSlotReviews && (
          <Link
            href={Routes.slotPage(getItemIdentifier(reviewItem))}
            className={styles.slot_review__visit}
          >
            <span>{'visit slot page'}</span>
            <Icon.Arrow width={20} height={20} />
          </Link>
        )}

        {underReview && (
          <div className={styles.slot_review__under_review}>
            <Icon.ExclamationMark fill="white" />
            <span>{'under review'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Filter = ({
  providers,
  onProviderChange,
  onSearchChange,
  resetSignal,
}: {
  providers: IProvider[];
  onProviderChange: (provider: IProvider | null) => void;
  onSearchChange: (search: string) => void;
  resetSignal?: unknown;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<IProvider | null>(
    null
  );
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDropdownOpen(false);
  }, [resetSignal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProviderSelect = (provider: IProvider | null) => {
    setSelectedProvider(provider);
    setIsDropdownOpen(false);
    onProviderChange(provider);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    onSearchChange(value);
  };

  return (
    <>
      <div className={styles.provider_dropdown_container} ref={dropdownRef}>
        <div
          className={styles.provider_dropdown_header}
          onClick={() => {
            setIsDropdownOpen(!isDropdownOpen);
          }}
        >
          <div className={styles.provider_dropdown_text}>
            {selectedProvider ? selectedProvider.name : 'Choose a provider'}
          </div>
          <div
            className={styles.provider_dropdown_icon}
            style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Icon.Arrow />
          </div>
        </div>
        {isDropdownOpen && (
          <div className={styles.provider_dropdown_menu}>
            <div
              className={styles.provider_dropdown_item}
              onClick={() => handleProviderSelect(null)}
            >
              All Providers
            </div>
            {providers.map((provider) => (
              <div
                key={provider._id}
                className={styles.provider_dropdown_item}
                onClick={() => handleProviderSelect(provider)}
              >
                {provider.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.filter_container}>
        <div className={styles.filter_divider}></div>
        <div className={styles.search_container}>
          <div className={styles.search_icon}>
            <Icon.Search />
          </div>
          <input
            type="text"
            value={searchText}
            onChange={handleSearch}
            placeholder="Find a slot..."
            className={styles.search_input}
          />
        </div>
      </div>
    </>
  );
};

export const Reviews = ({
  name,
  count,
  results,
  casino,
  noLimit,
  limit = 6,
  isProviderVisible,
}: {
  name?: string;
  results?: ISlotReview[];
  noLimit?: boolean;
  limit?: number;
  casino?: ICasino;
  count?: string | number;
  isProviderVisible?: boolean;
  slot?: ISlot | ISlotReview;
}) => {
  const [page, setPage] = useState(1);
  const [isSlotReviewVisible, setSlotReviewVisible] = useState(false);
  const { isLoggedIn } = useAuth();
  const [selectedProvider, setSelectedProvider] = useState<IProvider | null>(
    null
  );
  const [searchText, setSearchText] = useState('');

  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  useRedirectActions({
    actions: [
      {
        name: RedirectActions.WriteCasinoSlotReview,
        action: () => setSlotReviewVisible(true),
      },
    ],
  });

  const filteredResults = useMemo(() => {
    let filtered = [...(results ?? [])];

    // Filter by provider
    if (selectedProvider) {
      filtered = filtered.filter(
        (item) => item.provider && item.provider._id === selectedProvider._id
      );
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.slot && item.slot.name.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [results, selectedProvider, searchText]);

  const groupedData = useMemo(() => {
    const sortedData = [...filteredResults].sort((a, b) => {
      if (a!.provider!._id < b!.provider!._id) {
        return -1;
      }
      if (a!.provider!._id > b!.provider!._id) {
        return 1;
      }
      return 0;
    });

    return groupByProvidersAndSlots(
      noLimit ? sortedData : sortedData.slice(0, page * limit)
    );
  }, [filteredResults, limit, page, noLimit]);

  const allProviders = useMemo(() => {
    return results
      ?.map((item) => item.provider)
      .filter(
        (provider, index, self) =>
          provider &&
          index === self.findIndex((p) => p && p._id === provider._id)
      ) as IProvider[];
  }, [results]);

  if (!casino) {
    return null;
  }

  return (
    <div
      id={Anchor.RtpReviews}
      className={clsx(
        styles.container_reviews,
        isSlotReviewVisible && styles.container_reviews_modalOpen
      )}
    >
      <div className={styles.container_s}>
        <div id={Anchor.slot.reviews} className="anchor" />
        <div className={styles.header}>
          {name && (
            <Header
              isBigName
              id={Analytics.casino.allRtpReviews}
              hasIcon={false}
              name={name}
              count={count}
              isRtpReviews
              url={Routes.casinoSlotReviewsPage(getItemIdentifier(casino))}
            />
          )}
        </div>

        <div className={styles.action}>
          {!!results?.length && !isSlotReviewVisible && (
            <>
              <Filter
                providers={allProviders}
                onProviderChange={setSelectedProvider}
                onSearchChange={setSearchText}
                resetSignal={isSlotReviewVisible}
              />
              {!casino?.isClosed && (
                <Button
                  id={Analytics.casino.inspectAddSlotRtpReview}
                  icon={<Icon.SerachPlus />}
                  text={'Add slot RTP review'}
                  onClick={() => {
                    if (isLoggedIn) {
                      setSlotReviewVisible(true);
                    } else {
                      saveRedirectOptions({
                        url:
                          Routes.casinoPage(getItemIdentifier(casino)) +
                          `#${Anchor.Inspect}`,
                        action: RedirectActions.WriteCasinoSlotReview,
                      });
                      handleVisibleLoginModal();
                    }
                  }}
                  className={styles.slot_review__action}
                />
              )}
            </>
          )}
        </div>
        <CasinoSlotReviewModal
          casino={casino}
          isVisible={isSlotReviewVisible && isLoggedIn}
          setVisible={setSlotReviewVisible}
        />
        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />

        {!results?.length && (
          <div className={styles.noReviewsFound}>
            <Icon.Smile />
            <div>No slot RTP reviews yet. Be the first to add one!</div>
            {!casino?.isClosed && (
              <Button
                id={Analytics.casino.inspectAddSlotRtpReview}
                icon={<Icon.SerachPlus />}
                text={'Add slot RTP review'}
                onClick={() => {
                  if (isLoggedIn) {
                    setSlotReviewVisible(true);
                  } else {
                    saveRedirectOptions({
                      url:
                        Routes.casinoPage(getItemIdentifier(casino)) +
                        `#${Anchor.Inspect}`,
                      action: RedirectActions.WriteCasinoSlotReview,
                    });
                    handleVisibleLoginModal();
                  }
                }}
                className={styles.slot_review__action}
              />
            )}
          </div>
        )}

        {searchText?.length || selectedProvider ? (
          <>
            {filteredResults?.length ? (
              <div className={styles.filterInfo}>
                Found {filteredResults.length} reviews on{' '}
                {Array.from(filteredResults.map((item) => item.provider?.name))
                  .filter(
                    (provider, index, self) =>
                      provider &&
                      index === self.findIndex((p) => p && p === provider)
                  )
                  .map((item) => {
                    return (
                      <span className={styles.providerTag} key={item}>
                        {item}
                      </span>
                    );
                  })}
              </div>
            ) : (
              <div className={styles.noReviewsFound}>
                <Icon.Smile />
                <div>We didn’t find anything with the selected filters</div>
                <div
                  className={styles.clear_button}
                  onClick={() => {
                    setSearchText('');
                    setSelectedProvider(null);
                  }}
                >
                  Clear filters
                </div>
              </div>
            )}
          </>
        ) : null}

        {!!results?.length && (
          <div className={clsx(!isProviderVisible && styles.reviews__row)}>
            {Object.entries(groupedData).map(([, slotReviewsGroup]) => (
              <>
                {isProviderVisible && (
                  <div className={styles.reviews_provider}>
                    <Link
                      href={Routes.provider(
                        getItemIdentifier(
                          Object.values(slotReviewsGroup)?.[0]?.[0]?.provider
                        )
                      )}
                    >
                      <div className={styles.provider_logo}>
                        <Image
                          width={110}
                          height={40}
                          src={
                            Object.values(slotReviewsGroup)?.[0]?.[0]?.provider
                              ?.imageUrl ?? ''
                          }
                          alt={
                            Object.values(slotReviewsGroup)?.[0]?.[0]?.provider
                              ?.imageUrl ?? ''
                          }
                        />
                      </div>
                    </Link>
                    <div>
                      <Link
                        href={Routes.provider(
                          getItemIdentifier(
                            Object.values(slotReviewsGroup)?.[0]?.[0]?.provider
                          )
                        )}
                        className={styles.reviews_provider_link}
                      >
                        {
                          Object.values(slotReviewsGroup)?.[0]?.[0]?.provider
                            ?.name
                        }
                      </Link>{' '}
                      <span className={styles.reviews_provider_text}>
                        {`slots ${casino.name ?? ''} reviews`}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  className={clsx(
                    isProviderVisible && styles.reviews__row,
                    isProviderVisible && styles.reviews_group_divider
                  )}
                >
                  {Object.values(slotReviewsGroup).map((review) => {
                    const [data] = review ?? [];

                    return (
                      <>
                        <div style={{ marginTop: 10 }} />
                        <ReviewSlot
                          data={data}
                          slot={data.slot}
                          casino={data.casino}
                          key={data.slot?._id.toString() + '1'}
                          isPublicProfile
                          variant={'user'}
                        />
                      </>
                    );
                  })}
                </div>
              </>
            ))}
          </div>
        )}

        {page * limit <= filteredResults?.length && !noLimit && (
          <LoadMoreButton
            isAuto={false}
            isVisible={true}
            onClick={() => setPage(page + 1)}
            isFetching={false}
            className={styles.loadmoreButton}
            description="Load more"
            variant="transparent"
          />
        )}
      </div>
    </div>
  );
};
